import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

export type TUtcTime = 'hourMin' | 'hourMinSec' | 'dateTime';
export const DATE_FORMAT = {
  short: 'MM/dd/yy',
  shortWithYearsFull: 'MM/dd/yyyy',
  medium: 'MMMM d, yyyy',
  mediumTwoDigitsDay: 'MMM dd, yyyy',
  messageReceivedToday: 'hh:mm a',
  messageReceivedEarlier: "MMM dd yyyy 'at' hh:mm a",
  numericDateCommaTime: 'MM/dd/yyyy, hh:mm aaa',
  dateDotTimeWithMonthAbbreviation: 'MMM d, yyyy · hh:mm z',
  dateDotTimeWithMonthAbbreviationAndDay: 'EEEE, MMM d, yyyy · hh:mm z',
};

export const fromISOToShortDate = (isoDate: string): string => {
  if (isoDate === '') {
    return '';
  }

  const result = isoDate.split('T')[0];

  if (parseISO(result).toString() === 'Invalid Date') {
    throw new Error('Invalid time value');
  }

  return result;
};

export const fromShortDateToISO = (shortDate: string): string => {
  if (shortDate === '') {
    return '';
  }
  return new Date(shortDate).toISOString();
};

export const formatDate = (
  date?: number | Date,
  template = DATE_FORMAT.short
) => {
  if (date === undefined) {
    return '';
  }

  if (typeof date === 'number') {
    const time = new Date(date * 1000);

    return format(time, template);
  }

  return format(date, template);
};

/**
 * Formats a future date/time in a user-friendly relative format using Intl API
 * Uses relative time (e.g., "in 3 hours") for times within 24 hours
 * Otherwise shows formatted date/time in user's local timezone
 *
 * Examples:
 * - Within 24 hours: "in 3 hours"
 * - Within 48 hours: "tomorrow at 12:00 PM"
 * - Beyond 48 hours: "January 31 at 12:00 PM"
 * - Past times: "shortly"
 */
export const formatRelativeTime = (dateTime: Date): string => {
  const now = new Date();
  const diffMs = dateTime.getTime() - now.getTime();

  // If time is in the past, handle gracefully
  if (diffMs <= 0) {
    return 'shortly';
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  // For times less than 24 hours away, use Intl.RelativeTimeFormat
  if (diffHours < 24) {
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

    if (diffMinutes < 60) {
      return rtf.format(diffMinutes, 'minute');
    }

    return rtf.format(diffHours, 'hour');
  }

  // Check if time is tomorrow
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

  const endOfTomorrow = new Date(startOfTomorrow);
  endOfTomorrow.setDate(endOfTomorrow.getDate() + 1);

  if (dateTime >= startOfTomorrow && dateTime < endOfTomorrow) {
    // Within tomorrow - show "tomorrow at HH:MM AM/PM"
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `tomorrow at ${timeFormatter.format(dateTime)}`;
  }

  // More than 48 hours away - show full date using Intl.DateTimeFormat
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return dateFormatter.format(dateTime).replace(',', ' at');
};
